import { ArrowPathIcon } from '@heroicons/react/16/solid';
import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Description, Label } from '@oysterjs/ui/fieldset';
import { Spinner } from '@oysterjs/ui/spinner';
import React from 'react';
import { downloadCsv, downloadCsvText } from '~/lib/csv';
import { FileProblems } from './file-problems';
import { type CommitHandler, type SelectedChanges } from './model/commit-contract';
import { diffCsv } from './model/diff';
import { parseCsv } from './model/parse';
import { type CsvSchema } from './model/schema';
import { exportFilename, generateTemplate, templateFilename } from './model/template';
import { malformedCount, selectedChanges, selectedCount } from './model/selection';
import { headerProblem } from './model/validate';
import { CsvReconcileTable } from './table/reconcile-table';
import { CsvUpload } from './upload';
import { useCsvReview } from './use-csv-review';

/** Worded so a partial import is not mistaken for success. */
const describePartial = (applied: number, failed: number): string =>
  `${applied} change${applied === 1 ? '' : 's'} applied, ${failed} could not be. ` +
  `The ones that failed are still selected, so you can try them again.`;

const describeImport = (count: number): string => `Import ${count} ${count === 1 ? 'row' : 'rows'}`;

/** Why Import is blocked when a file adds more rows than `addable`, and how many to skip. */
const describeOverLimit = (adding: number, addable: number, limitNote?: string): string => {
  const allowed = addable === 0 ? 'none can be added' : `only ${addable} can be added`;
  const everyRow = adding === 1 ? 'it' : 'them';
  const toSkip = addable === 0 ? everyRow : `${adding - addable} of them`;
  return (
    `This file adds ${adding} ${adding === 1 ? 'row' : 'rows'}, but ${allowed}. ` +
    `Skip ${toSkip}${limitNote ? `, or ${limitNote}` : ''}.`
  );
};

/**
 * Bulk CSV import: template, upload, validation, review and commit. The component works
 * out what changed; the consumer's `onCommit` writes it.
 */
export const CsvImport = ({
  listName,
  schema,
  currentData = [],
  treatAbsentAsRemoval: removalByDefault = false,
  offerRemoval = false,
  addOnly = false,
  maxAdded,
  limitNote,
  upToDateMessage,
  describeCommit,
  commitLabel = describeImport,
  onCommit,
  onCommitted,
  onCommittingChange
}: {
  listName: string;
  /** Memoize it: a new schema checks an open file again. */
  schema: CsvSchema;
  /** Records the list already holds, which the upload is reconciled against. */
  currentData?: Record<string, string>[];
  /** Whether a record missing from the upload should be offered for deletion. */
  treatAbsentAsRemoval?: boolean;
  /** Lets the user toggle deletion; treatAbsentAsRemoval is then only the default. */
  offerRemoval?: boolean;
  /**
   * Only add records: a row matching one already in the list is left as it is. With nothing
   * existing to change, Export and the confirm step are left out and the button writes at once.
   */
  addOnly?: boolean;
  /**
   * How many more records the list can take; each record the import removes makes room
   * for one more. Adding more blocks Import until rows are skipped.
   */
  maxAdded?: number;
  /** Follows the explanation when `maxAdded` is exceeded, e.g. how to raise the limit. */
  limitNote?: string;
  /** Shown in place of the review when every row is already in the list. */
  upToDateMessage?: string;
  /** What committing does beyond the list itself, such as who can now sign in; shown before confirming. */
  describeCommit?: (changes: SelectedChanges) => string | undefined;
  /** The button's text for applying `count` selected rows. */
  commitLabel?: (count: number) => string;
  /** Performs the write. Omitted while a consumer only wants the review. */
  onCommit?: CommitHandler;
  /** Called once anything has been written, so the list can reload. */
  onCommitted?: () => void;
  /** Called as a write starts and ends, so the page can hold back its own edits meanwhile. */
  onCommittingChange?: (committing: boolean) => void;
}) => {
  const [committing, setCommitting] = React.useState(false);
  /** How many changes a long write has finished, once the consumer reports any. */
  const [written, setWritten] = React.useState<number>();
  const [error, setError] = React.useState<string>();
  const [partial, setPartial] = React.useState<string>();
  const [failures, setFailures] = React.useState<Record<string, string>>({});
  const [confirmingRemoval, setConfirmingRemoval] = React.useState(false);
  // Kept after the dialog is answered, since it is still on screen as it fades out.
  const [removalCount, setRemovalCount] = React.useState(0);

  const {
    filename,
    reading,
    setReading,
    validation,
    diff,
    selected,
    setSelected,
    confirming,
    setConfirming,
    listMoved,
    treatAbsentAsRemoval,
    setTreatAbsentAsRemoval,
    load,
    loadHeader,
    recordEdit,
    recheck,
    startConfirming,
    reset: resetReview
  } = useCsvReview({ schema, currentData, removalByDefault, addOnly, committing });

  const committingChanged = React.useRef(onCommittingChange);
  committingChanged.current = onCommittingChange;
  React.useEffect(() => committingChanged.current?.(committing), [committing]);

  // A write sent in parts stops at whichever part is in flight when the page unloads.
  // Moving elsewhere in the app does not unload it, so the write carries on and needs no
  // warning.
  React.useEffect(() => {
    if (!committing) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [committing]);

  const reset = () => {
    resetReview();
    setError(undefined);
    setPartial(undefined);
    setFailures({});
  };

  const chosen = diff ? selectedChanges(diff, selected) : undefined;
  const chosenCount = chosen ? selectedCount(chosen) : 0;
  const malformed = diff ? malformedCount(diff, selected) : 0;
  const deleting = chosen?.removed.length ?? 0;
  const addable = maxAdded === undefined ? undefined : maxAdded + deleting;
  const overLimit = addable !== undefined && chosen !== undefined && chosen.added.length > addable;
  const sensitiveChanges = schema.fields
    .filter((field) => field.sensitive)
    .map((field) => ({
      label: field.label,
      count: (chosen?.updated ?? []).filter((change) =>
        change.fieldChanges?.some((fieldChange) => fieldChange.key === field.key)
      ).length
    }))
    .filter(({ count }) => count > 0);
  const commitNote = chosen && describeCommit?.(chosen);

  const commit = async () => {
    // A double click can land two events before React disables the button.
    if (!onCommit || !chosen || committing || selectedCount(chosen) === 0) {
      return;
    }

    setCommitting(true);
    setError(undefined);
    setPartial(undefined);
    setFailures({});

    try {
      const result = await onCommit(chosen, setWritten);
      switch (result.status) {
        case 'failed':
          // Nothing was written, so the selection is still exactly what to retry.
          setError(result.error);
          return;
        case 'partial': {
          const failed = result.failures;
          // Keep only the failures selected so a retry is one click.
          setFailures(Object.fromEntries(failed.map((failure) => [failure.id, failure.message])));
          setSelected(new Set(failed.map((failure) => failure.id)));
          setPartial(describePartial(selectedCount(chosen) - failed.length, failed.length));
          onCommitted?.();
          return;
        }
        case 'applied':
          onCommitted?.();
          reset();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Something went wrong applying changes.');
    } finally {
      setCommitting(false);
      setWritten(undefined);
    }
  };

  return (
    <div className="min-w-0 space-y-4">
      <div>
        <h3 className="my-0 text-sm font-semibold text-neutral-900 dark:text-white">
          Import {listName.toLowerCase()}
        </h3>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
          Start from the template. It lists every column and the values each one accepts. Rows
          starting with # are examples and are skipped.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          outline
          onClick={() => downloadCsvText(generateTemplate(schema), templateFilename(listName))}
        >
          Download template
        </Button>

        {/* Re-uploading an edited export reconciles as updates, not duplicates. */}
        {!addOnly && currentData.length > 0 && (
          <Button
            outline
            onClick={() =>
              downloadCsv(
                [
                  schema.fields.map((field) => field.label),
                  ...currentData.map((record) =>
                    schema.fields.map((field) => record[field.key] ?? '')
                  )
                ],
                exportFilename(listName)
              )
            }
          >
            {`Export ${listName.toLowerCase()}`}
          </Button>
        )}

        {validation && !confirming && (
          <Button outline disabled={committing} onClick={reset}>
            <ArrowPathIcon data-slot="icon" />
            Choose a different file
          </Button>
        )}
      </div>

      {!validation && reading && (
        <div
          role="status"
          className="flex items-center justify-center gap-3 rounded-lg border border-dashed border-neutral-300 p-6 text-sm text-neutral-600 dark:border-neutral-600 dark:text-neutral-300"
        >
          <Spinner color="neutral" size="size-4" />
          Reading {reading}…
        </div>
      )}

      {!validation && !reading && (
        <CsvUpload
          onReading={setReading}
          onFile={load}
          checkHeader={(head) => headerProblem(schema, parseCsv(head).header)?.message}
          onReject={loadHeader}
        />
      )}

      {validation && (
        <div className="min-w-0 space-y-4">
          <p className="min-w-0 truncate text-xs font-medium text-neutral-700 dark:text-neutral-200">
            {filename}
          </p>

          <FileProblems result={validation} />

          {diff && validation.structuralErrors.length === 0 && (
            <>
              <CsvReconcileTable
                schema={schema}
                result={diff}
                selected={selected}
                onSelectedChange={setSelected}
                failures={failures}
                // Frozen while confirming, so what is confirmed is what is shown.
                disabled={committing || confirming}
                onCellChange={recordEdit}
                onCellLeave={recheck}
                upToDateMessage={upToDateMessage}
              />

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
                >
                  {error} Your selection has been kept.
                </div>
              )}

              {partial && (
                <div
                  role="alert"
                  className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
                >
                  {partial}
                </div>
              )}

              {onCommit &&
                (confirming ? (
                  <div className="space-y-2">
                    <p className="text-sm text-neutral-700 dark:text-neutral-200">
                      {malformed > 0
                        ? `${malformed} row${malformed === 1 ? '' : 's'} will not be imported because ${
                            malformed === 1 ? 'it has' : 'they have'
                          } errors that need correcting. Everything else will be applied.`
                        : `This updates ${listName.toLowerCase()} with the rows you have reviewed.`}
                      {deleting > 0 &&
                        ` ${deleting} ${deleting === 1 ? 'entry' : 'entries'} not in the file will be deleted.`}
                      {sensitiveChanges.map(
                        ({ label, count }) =>
                          ` ${count} ${count === 1 ? 'row changes' : 'rows change'} ${label}.`
                      )}
                      {commitNote && ` ${commitNote}`}
                    </p>
                    <div className="flex items-center gap-2">
                      {committing && written !== undefined && (
                        <p role="status" className="text-sm text-neutral-500 dark:text-neutral-400">
                          Importing {written} of {chosenCount}…
                        </p>
                      )}
                      {/* Disabled, not hidden, so a second click cannot land on a replacement. */}
                      <Button
                        color="white"
                        disabled={committing}
                        onClick={() => setConfirming(false)}
                      >
                        Back
                      </Button>
                      <Button
                        // Names the button while its text gives way to the spinner.
                        loading={committing}
                        aria-label={committing ? 'Importing' : undefined}
                        disabled={committing || chosenCount === 0}
                        onClick={commit}
                      >
                        {committing
                          ? ''
                          : `Confirm ${chosenCount} ${chosenCount === 1 ? 'row' : 'rows'}`}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {listMoved && (
                      <p role="status" className="text-sm text-amber-800 dark:text-amber-200">
                        {listName} changed while you were confirming, so this review was updated.
                        Check it, then import again.
                      </p>
                    )}
                    {overLimit && (
                      <p role="status" className="text-sm text-red-700 dark:text-red-300">
                        {describeOverLimit(chosen.added.length, addable, limitNote)}
                      </p>
                    )}
                    <Button
                      loading={committing}
                      disabled={committing || chosenCount === 0 || overLimit}
                      onClick={addOnly ? commit : startConfirming}
                    >
                      {chosenCount === 0 ? 'No rows to import' : commitLabel(chosenCount)}
                    </Button>
                  </div>
                ))}

              {/* Hidden while confirming; the confirmation already states the deletions. */}
              {offerRemoval && !confirming && (
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={treatAbsentAsRemoval}
                    disabled={committing}
                    onChange={(on: boolean) => {
                      // Only turning deletions on asks for confirmation.
                      if (!on) {
                        setTreatAbsentAsRemoval(false);
                        return;
                      }
                      const withRemovals = validation
                        ? diffCsv(schema, validation, currentData, { treatAbsentAsRemoval: true })
                        : undefined;
                      setRemovalCount(
                        withRemovals?.changes.filter(
                          (change) => change.classification === 'removed'
                        ).length ?? 0
                      );
                      setConfirmingRemoval(true);
                    }}
                  />
                  <Label>This file is the whole {listName.toLowerCase()}</Label>
                  <Description>
                    Entries that are not in the file will be deleted. Leave this off to only add and
                    update entries.
                  </Description>
                </CheckboxField>
              )}

              <Dialog
                open={confirmingRemoval}
                onClose={() => setConfirmingRemoval(false)}
                size="md"
              >
                <DialogTitle>Delete entries that aren't in this file?</DialogTitle>
                <DialogDescription>
                  {removalCount === 0
                    ? `Every entry in ${listName.toLowerCase()} is in ${filename}, so nothing would be deleted now.`
                    : `${removalCount} ${removalCount === 1 ? 'entry' : 'entries'} in ${listName.toLowerCase()} ${
                        removalCount === 1 ? "isn't" : "aren't"
                      } in ${filename} and will be marked for deletion.`}
                </DialogDescription>
                <DialogBody>
                  <p className="text-sm text-neutral-700 dark:text-neutral-200">
                    They'll be listed under Delete, where you can keep any of them. Nothing is
                    deleted until you confirm the import and save the page.
                  </p>
                </DialogBody>
                <DialogActions>
                  <Button color="white" onClick={() => setConfirmingRemoval(false)}>
                    Cancel
                  </Button>
                  <Button
                    color="sky"
                    onClick={() => {
                      setConfirmingRemoval(false);
                      setTreatAbsentAsRemoval(true);
                    }}
                  >
                    I understand
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </div>
      )}
    </div>
  );
};
